import {
  CreateResourceAction,
  UpdateResourceAction,
  DeleteResourceAction,
} from "../../../../../store/action";
import { resourceEmailToSave } from "./validation";

/**
 * Resource management utilities
 */

export const createNewResource = () => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  return {
    type: "Person",
    name: "",
    email: "",
    id: Date.now(),
    scheduleMode: "weekly",
    bookingAdvanceDays: 30,
    workingDays: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
    },
    workingHours: {
      start: "09:00",
      end: "17:00",
    },
    useAdvancedSchedule: false,
    advancedSchedule: {
      monday: { enabled: true, start: "09:00", end: "17:00" },
      tuesday: { enabled: true, start: "09:00", end: "17:00" },
      wednesday: { enabled: true, start: "09:00", end: "17:00" },
      thursday: { enabled: true, start: "09:00", end: "17:00" },
      friday: { enabled: true, start: "09:00", end: "17:00" },
      saturday: { enabled: false, start: "09:00", end: "17:00" },
      sunday: { enabled: false, start: "09:00", end: "17:00" },
    },
    monthlySchedule: {
      currentMonth: currentMonth,
      currentYear: currentYear,
      daysOff: [],
      customDays: {},
      timeOff: [],
    },
  };
};

/**
 * Saves each resource in order, creating new ones and updating existing
 * ones. Throws on the first failure — same as before — but now calls
 * `onItemSaved(originalResource, result)` right after each individual save
 * succeeds, so the caller can merge the server-assigned id back into its own
 * state incrementally instead of waiting for the whole loop to finish.
 *
 * This matters on a partial failure (e.g. hitting the plan's seat cap on
 * item 3 of 4): without the per-item callback, items 1-2 keep their
 * client-side temporary ids forever, and `isDbId` below misclassifies them
 * as new on the next retry — creating duplicates.
 */
export const saveResources = async (resources, onItemSaved) => {
  const results = [];

  for (const resource of resources) {
    const isDbId = typeof resource.id === "number" && resource.id < 1000000;

    const resourceData = {
      type: resource.type,
      name: resource.name,
      // Only a Person shows the email field. A Space that was a Person a moment
      // ago still holds what was typed, and sending it got the save refused
      // over a field nobody could see.
      email: resourceEmailToSave(resource),
      scheduleMode: resource.scheduleMode,
      bookingAdvanceDays: resource.bookingAdvanceDays,
      workingDays: resource.workingDays,
      workingHours: resource.workingHours,
      useAdvancedSchedule: resource.useAdvancedSchedule,
      advancedSchedule: resource.advancedSchedule,
      monthlySchedule: resource.monthlySchedule,
      // Sent back as loaded. An update without it reads as "unassigned" and
      // drops the link to whoever staffs the resource — the owner included,
      // once their own email has linked them to it.
      userId: resource.userId ?? null,
    };

    try {
      let result;
      if (isDbId) {
        result = await UpdateResourceAction(resource.id, resourceData);
      } else {
        result = await CreateResourceAction(resourceData);
      }
      results.push(result);
      onItemSaved?.(resource, result);
    } catch (error) {
      console.error(`Error saving resource ${resource.name}:`, error);
      // Which one failed, so the wizard can mark that resource's fields rather
      // than report a failure somewhere in the list.
      error.failedResource = resource;
      throw error;
    }
  }

  return results;
};

export const deleteResource = async (id) => {
  const isDbId = typeof id === "number" && id < 1000000;

  if (isDbId) {
    await DeleteResourceAction(id);
  }
};
